import * as React from 'react'

import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@material-ui/core'

import { useDropzone } from 'react-dropzone'
import { useUploadMarketplaceListingFeatureImage } from '../hooks/useUploadMarketplaceListingFeatureImage'

type MarketplaceMediaDetailModalProps = IMarketplaceMediaDetailModalProps

interface IMarketplaceMediaDetailModalProps {
  marketplaceListingId: string | null
  open: boolean
  onClose: () => void
  onSave?: (imageUrl: string) => void
  aspectRatio?: number
  isNote?: boolean
}

export const MarketplaceMediaDetailModal: React.FC<MarketplaceMediaDetailModalProps> = props => {
  const title = 'Upload Marketplace Feature Image'
  const [uploadMarketplaceFeatureImage] = useUploadMarketplaceListingFeatureImage()
  const [saving, setSaving] = React.useState(false)
  const [, setUploadError] = React.useState(false)
  const [, setUploaded] = React.useState(false)
  const [image, setImage] = React.useState<URL | null>(null)

  const fetchImageFromUri = React.useCallback(async (uri: URL | string) => {
    const response = await fetch(uri)
    const blob = await response.blob()
    return blob
  }, [])

  const uploadToUrl = React.useCallback(async (img: Blob, uploadUrl: string) => {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': img.type
      },
      body: img
    })

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`)
    }

    return response.text()
  }, [])

  const save = async () => {
    if (!props.marketplaceListingId) {
      return
    }

    setSaving(true)
    const result = await uploadMarketplaceFeatureImage({
      variables: {
        input: {
          marketplaceListingId: props.marketplaceListingId,
          isNote: props.isNote ?? false
        }
      }
    })

    if (!result.data) throw Error()
    if (!image) throw Error()

    const uploadResult = result.data.uploadMarketplaceFeatureImageV2

    if (uploadResult.__typename === 'MarketplaceGalleryErrorV2') {
      setUploadError(true)
      setSaving(false)
      return
    }

    if (uploadResult.__typename !== 'MarketplaceGalleryUploadV2') {
      setUploadError(true)
      setSaving(false)
      return
    }

    const img = await fetchImageFromUri(image!)
    await uploadToUrl(img, uploadResult.uploadUrl)
    setSaving(false)
    setUploaded(true)
    props?.onSave?.(image.toString())
  }

  return (
    <Dialog maxWidth='lg' open={props.open} onClose={() => props.onClose()}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <CroppableImageDropzone aspectRatio={props.aspectRatio ?? 1} onChange={setImage} />
      </DialogContent>
      <DialogActions>
        {save && (
          <Button disabled={!image || !props.marketplaceListingId} onClick={save} variant='contained'>
            {saving ? <CircularProgress /> : 'Save'}
          </Button>
        )}
        <Button onClick={() => props.onClose()} variant='outlined'>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

interface ICroppableImageDropzoneProps {
  aspectRatio?: number
  onChange: (blob: URL) => void
}

type CroppableImageDropzoneProps = ICroppableImageDropzoneProps

const CroppableImageDropzone: React.FC<CroppableImageDropzoneProps> = props => {
  const [files, setFiles] = React.useState<any[]>([])

  const onDrop = React.useCallback((acceptedFiles: File[]) => {
    setFiles(
      acceptedFiles.map((file: any) =>
        Object.assign(file, {
          preview: URL.createObjectURL(file)
        })
      )
    )
  }, [])
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: 1,
    accept: 'image/jpeg, image/png, image/webp',
    onDrop,
    noClick: true
  })

  const preview = files[0]?.preview

  React.useEffect(() => {
    props.onChange(files[0]?.preview)
  }, [files, props])

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {preview ? (
        <Box style={{ width: 400 }}>
          <img src={preview} alt='preview' style={{ maxWidth: '100%' }} />
        </Box>
      ) : (
        <Box style={{ width: 400, border: '2px dotted black' }}>
          {isDragActive ? <p>Drop here</p> : <p>Drag and drop an image file here</p>}
        </Box>
      )}
    </div>
  )
}
